import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';
import { Link } from 'react-router-dom';
import { RootState } from 'modules';
import { Typography, Button } from 'antd';
import styled from 'styled-components';
import { device, PrimaryColor, SecondaryColor, size } from 'GlobalStyles';
import dot from 'assets/icons/dot.svg';

let { Title, Text } = Typography;

interface IMainIntroduction {
  firstArticle?: boolean;
  bg?: boolean;
  responsiveBg?: boolean;
}

const MainIntroduction = ({ contents, caseQuiz, difImg }: any) => {
  let { title, subTitle, description, btnTitle, guidePrice } = contents;
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  return (
    <StyledContainer>
      <MainIntroductionHeader>
        <div>
          <Title>{title}</Title>
          <Text>{subTitle}</Text>
        </div>
        <div className='btnArea'>
          <Link to={btnTitle.guideLink}>
            <Button type='primary'>{btnTitle.title}</Button>
          </Link>
          {btnTitle.subTitle && <div className='subTitle'>* 수강권 구매 후 이용가능</div>}
        </div>
      </MainIntroductionHeader>

      <Descriptions>
        {description.map(({ title, explanation, thumbnail, thumbnail2 }: any, i: number) => (
          <>
            <Description>
              {!difImg ? (
                <DetailIntroductionImg src={thumbnail} />
              ) : (
                <>
                  <MediaQuery minWidth={size.lg + 1}>
                    <DetailIntroductionImg
                      src={thumbnail2}
                      // style={{ display: 'block', width: '320px', margin: '0 auto' }}
                    />
                  </MediaQuery>
                  <MediaQuery maxWidth={size.lg}>
                    <DetailIntroductionImg
                      src={thumbnail}
                      style={{ border: '1px solid #999999', borderRadius: '12px' }}
                    />
                  </MediaQuery>
                </>
              )}
              <MediaQuery minWidth={size.lg + 1}>
                <DetailIntroductionTitle
                  style={{ textAlign: difImg && 'center', fontSize: difImg && '18px' }}
                >
                  {title}
                </DetailIntroductionTitle>
              </MediaQuery>
              {!difImg && (
                <MediaQuery maxWidth={size.lg}>
                  <DetailIntroductionTitle>{title}</DetailIntroductionTitle>
                </MediaQuery>
              )}
              {explanation.map((ex: string) => (
                <>
                  <MediaQuery minWidth={size.lg + 1}>
                    <Explanations style={{ justifyContent: difImg && 'center' }}>
                      {!difImg && (
                        <div className='bullet'>
                          <img src={dot} alt='bullet' />
                        </div>
                      )}
                      <DetailExplanation
                        style={{ textAlign: difImg && 'center', fontSize: difImg && '15px' }}
                      >
                        {ex}
                      </DetailExplanation>
                    </Explanations>
                  </MediaQuery>
                  {!difImg && (
                    <MediaQuery maxWidth={size.lg}>
                      <Explanations>
                        <div className='bullet'>
                          <img src={dot} alt='bullet' />
                        </div>
                        <DetailExplanation>{ex}</DetailExplanation>
                      </Explanations>
                    </MediaQuery>
                  )}
                </>
              ))}
            </Description>
            {difImg && i === 0 && <div className='plusIcon'>+</div>}
          </>
        ))}
      </Descriptions>

      {guidePrice && (
        <PriceGuideBox>
          <GuidePriceHeader>
            <Title>{guidePrice.title}</Title>
            {guidePrice.subTitle && <Text>* {guidePrice.subTitle}</Text>}
          </GuidePriceHeader>

          {guidePrice.subTitle2 && <Text className='secondSubTitle'>{guidePrice.subTitle2}</Text>}

          <GuidePriceTickets>
            {guidePrice.ticket.map((ticket: any) => (
              <div>
                <PriceGuideTicket>
                  <Title>{ticket.title}</Title>
                  {description && (
                    <Text>
                      {ticket.discount && <span className='discount'>정가 {ticket.discount}만원</span>}
                      <span className='price'>{ticket.price}</span>
                      <span className='priceTag'>만원</span> <span className='period'>/{ticket.period}</span>
                    </Text>
                  )}
                </PriceGuideTicket>

                {caseQuiz && currentUser?.is_drill_membership ? (
                  <Link to={'/case/quizzes'}>
                    <Button className='secondary purchased'>이미 구매하셨습니다</Button>
                  </Link>
                ) : (
                  <Link to={ticket.purchaseLink}>
                    <Button className='secondary'>구매하기</Button>
                  </Link>
                )}
              </div>
            ))}
          </GuidePriceTickets>
        </PriceGuideBox>
      )}
    </StyledContainer>
  );
};

export default React.memo(MainIntroduction);

const StyledContainer = styled.article<IMainIntroduction>`
  max-width: 1440px;
  margin: 0 auto;
  padding: 50px 50px 70px 50px;

  @media screen and ${device.lg} {
    padding: 35px 20px;
  }

  button.secondary {
    display: block;
    width: 134px;
    height: 44px;
    margin: 0 auto;

    @media screen and ${device.lg} {
      width: 100%;
    }
  }
`;

const MainIntroductionHeader = styled.header<IMainIntroduction>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and ${device.lg} {
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }

  h1.ant-typography {
    margin: 0 0 3px 0;
    font-size: 24px;
    color: ${({ bg }) => (bg ? '#ffffff' : SecondaryColor)};

    @media screen and ${device.lg} {
      font-size: 20px;
    }
  }

  span.ant-typography {
    display: inline-block;
    max-width: 800px;
    font-size: 20px;
    color: ${({ bg }) => (bg ? '#ffffff' : PrimaryColor)};

    @media screen and ${device.lg} {
      max-width: 100%;
      font-size: 16px;
    }
  }

  .btnArea {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    @media screen and ${device.lg} {
      width: 100%;
    }

    a {
      @media screen and ${device.lg} {
        width: 100%;
      }

      .ant-btn-primary {
        width: 100%;
        margin-top: 25px;
      }
    }

    .subTitle {
      margin-top: 4px;
      color: #e4e4e4;

      @media screen and ${device.lg} {
        display: none;
      }
    }
  }
`;

const DetailIntroductionImg = styled.img`
  width: 100%;

  @media screen and ${device.lg} {
    margin-top: 25px;
  }
`;

const DetailIntroductionTitle = styled.div<IMainIntroduction>`
  margin-top: 20px;
  font-size: 20px;
  font-weight: 700;
  color: ${({ bg }) => bg && '#ffffff'};

  @media screen and ${device.lg} {
    margin-top: 15px;
    font-size: 18px;
  }
`;

const DetailExplanation = styled.div<IMainIntroduction>`
  font-size: 18px;
  color: ${({ bg }) => bg && '#ffffff'};

  @media screen and ${device.lg} {
    font-size: 14px;
  }
`;

const Descriptions = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-top: 25px;

  @media screen and ${device.lg} {
    flex-direction: column;
    align-items: center;
  }

  .plusIcon {
    margin: 0 30px;
    font-size: 50px;
    line-height: 400px;

    @media screen and ${device.lg} {
      display: none;
    }
  }
`;

const Description = styled.div`
  max-width: 446px;
  flex: 1;
  padding: 0 16px;

  @media screen and ${device.lg} {
    max-width: 100%;
    width: 100%;
    padding: 0;
  }
`;

const Explanations = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  .bullet {
    margin-right: 7px;
  }
`;

const PriceGuideBox = styled.div`
  margin-top: 25px;
  padding: 30px 45px;
  border: 1px solid ${SecondaryColor};
  border-radius: 10px;
  background: #ffffff;

  @media screen and ${device.lg} {
    padding: 25px 15px;
  }

  .secondSubTitle {
    display: block;
    margin: 10px 0 0 0;
    color: ${PrimaryColor};
  }
`;

const GuidePriceHeader = styled.header`
  display: flex;
  align-items: center;

  @media screen and ${device.lg} {
    flex-direction: column;
    align-items: flex-start;
  }

  h1.ant-typography {
    margin: 0 10px 0 0;
    color: #141121;
    font-size: 20px;
  }

  span.ant-typography {
    color: #747474;
  }
`;

const GuidePriceTickets = styled.div`
  display: flex;
  justify-content: center;

  @media screen and ${device.lg} {
    flex-direction: column;
  }

  .ant-btn.secondary.purchased {
    width: auto;

    @media screen and ${device.lg} {
      width: 100%;
    }
  }
`;

const PriceGuideTicket = styled.div`
  width: 356px;
  margin: 15px 15px;
  padding: 25px 0;
  border-radius: 10px;
  background: #f9f9f9;
  white-space: pre-wrap;

  @media screen and ${device.xl} {
    width: 260px;
  }

  @media screen and ${device.lg} {
    width: 100%;
    margin: 25px 0;
  }

  h1.ant-typography {
    margin: 0;
    font-size: 18px;
    text-align: center;
    line-height: 26px;
    color: ${PrimaryColor};

    @media screen and ${device.xl} {
      font-size: 16px;
    }

    @media screen and ${device.lg} {
      font-size: 18px;
    }
  }

  span.ant-typography {
    display: block;
    text-align: center;

    .price {
      margin-right: 5px;
      font-size: 48px;
      color: ${SecondaryColor};
    }

    .priceTag {
      font-size: 18px;
      color: ${SecondaryColor};
    }

    .period,
    .discount {
      font-size: 18px;
      color: #747474;
    }

    .discount {
      margin-right: 10px;
      text-decoration: line-through;
    }
  }
`;
